/**
 * @file index.tsx
 *
 * @fileoverview Organization public profile page.
 */

import React from 'react';
import { GetServerSideProps, NextPage } from 'next';
import { OrganizationPublicProfile } from '~/jobiqo/components/Organization/Detail';
import { addApolloState } from '~/jobiqo/apollo/initApollo';
import { commonQueries } from '~/jobiqo/lib/data/commonQueries';
import { getServerConfigByName } from '~/jobiqo/lib/config';
import {
  getMiniSiteJobsPath,
  getMiniSiteSearchQuerystring,
  isMiniSiteJobSearchQuery,
} from '~/jobiqo/components/Organization/MiniSite/utils';
import { OrganizationProfile } from '~/jobiqo/types/types';
import {
  getOrganizationPageRequestContext,
  getOrganizationProfileResult,
  getOrganizationRouteResponse,
} from '~/jobiqo/pages/organization/shared/server';
import { stringify } from 'querystring';

interface OrganizationDetailPageProps {
  path: string;
  loading: boolean;
  error: boolean;
  data: any;
}

export const OrganizationDetailPage: NextPage<
  OrganizationDetailPageProps,
  {}
> = ({ path, loading, error, data }) => {
  return (
    <OrganizationPublicProfile
      path={path}
      loading={loading}
      error={error}
      data={data}
    />
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const {
    apolloClient,
    backendConfig,
    path,
    organizationPath,
  } = await getOrganizationPageRequestContext(ctx);
  const SITE_CAMPAIGN_ENABLED = !!getServerConfigByName(
    backendConfig,
    'site_campaign_enabled',
  )?.value[0];
  const ORG_MINISITE_ENABLED = !!getServerConfigByName(
    backendConfig,
    'jobiqo_organization_minisite',
  )?.value[0];
  const ORG_BLOG_ENABLED = !!getServerConfigByName(
    backendConfig,
    'jobiqo_blog_organization',
  )?.value[0];
  const organizationResult = await getOrganizationProfileResult({
    apolloClient,
    organizationPath,
    language: ctx.locale,
    minisiteEnabled: ORG_MINISITE_ENABLED,
    blogEnabled: ORG_BLOG_ENABLED,
  });
  const { loading, error, data } = organizationResult;
  const organizationRouteResponse = getOrganizationRouteResponse({
    data,
    error,
    organizationPath,
  });

  if (organizationRouteResponse) {
    return organizationRouteResponse;
  }

  const organization = data?.organizationProfileByPath
    ?.content as OrganizationProfile | null;

  if (organization?.isMinisiteEnabled && isMiniSiteJobSearchQuery(ctx.query)) {
    const querystring = getMiniSiteSearchQuerystring(ctx.query);
    const serializedQuerystring = stringify(querystring);

    return {
      redirect: {
        destination: `${getMiniSiteJobsPath(organizationPath)}${
          serializedQuerystring ? `?${serializedQuerystring}` : ''
        }`,
        permanent: false,
      },
      props: {},
    };
  }

  // Run all common queries like metatags and menus.
  await commonQueries(ctx, apolloClient, path, {
    siteCampaignEnabled: SITE_CAMPAIGN_ENABLED,
  });

  return addApolloState(apolloClient, {
    props: {
      path: organizationPath,
      data,
      loading,
      error: error ?? false,
    },
  });
};

export default OrganizationDetailPage;
